import React, { useState, useRef, useEffect } from "react";
import "./index.scss";
import classNames from "classnames";
import { DropDownIcon } from "../Icon";
import { Typography } from "../Typography";

type Option =
  | undefined
  | {
      group: string;
      label: string;
    };

interface SearchableDropdownProps {
  options: Option[];
  onSelect: (selectedOption: Option, child?: any) => void;
  placeholder?: string;
  isDisabled?: boolean;
  styles?: string;
}

export const SearchableDropdown = ({
  options,
  onSelect,
  placeholder = "Select an option",
  isDisabled,
  styles,
}: SearchableDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setFilteredOptions(
      options.filter(
        (option) =>
          option &&
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, options]);

  const handleOptionSelect = (option: Option, child?: Option) => {
    if(child) {
      setSelectedOption(child);
      onSelect(option, child);
    } else {
      setSelectedOption(option);
      onSelect(option, undefined);
    }
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleHeaderClick = () => {
    setIsOpen((prev) => !prev);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const groupedOptions = filteredOptions.reduce<{ [key: string]: Option[] }>(
    (acc, option) => {
      if (option) {
        if (!acc[option.group]) {
          acc[option.group] = [];
        }
        acc[option.group].push(option);
      }
      return acc;
    },
    {}
  );

  return (
    <div
      className={classNames("searchable-dropdown", {
        [`${styles}`]: styles,
      })}
      ref={dropdownRef}
    >
      <div
        className="dropdown-header"
        onClick={isDisabled ? undefined : handleHeaderClick}
      >
        {isOpen ? (
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            ref={inputRef}
            placeholder={placeholder}
            className="dropdown-input"
          />
        ) : (
          <div className={classNames("selected-item")}>
            <Typography
              styles="selected-text-custom-style"
              label={
                selectedOption && typeof selectedOption === "string"
                  ? selectedOption
                  : placeholder
              }
              variant="h8Book"
            />
          </div>
        )}
        <div
          className={classNames(
            "drop-down-icon",
            isOpen && "drop-down-icon-hide"
          )}
        >
          <DropDownIcon />
        </div>
      </div>
      {isOpen && (
        <div className={classNames("drop-down-item-wrapper")}>
          <div className="drop-down-item-list-container">
            {Object.keys(groupedOptions).map((option: any, index: number) => (
              <>
                <div
                  key={option && option}
                  onClick={() =>
                    isDisabled ? undefined : handleOptionSelect(option)
                  }
                  className={classNames({
                    "drop-down-item": true,
                    "first-item": index === 0,
                    "last-item":
                      Object.keys(groupedOptions)?.length === index + 1,
                    "only-item": Object.keys(groupedOptions)?.length === 1,
                  })}
                >
                  {option && option}
                </div>
                {groupedOptions[option].map((child: any) => (
                  <div
                    key={child && child}
                    onClick={() =>
                      isDisabled ? undefined : handleOptionSelect(option, child.label)
                    }
                    className={classNames({
                      "drop-down-item": true,
                      "child-item": true
                      // "first-item": index === 0,
                      // "last-item":
                      //   Object.keys(groupedOptions)?.length === index + 1,
                      // "only-item": Object.keys(groupedOptions)?.length === 1,
                    })}
                  >
                    {option === "Show all" ? null : child.label}
                  </div>
                ))}
              </>
            ))}
          </div>
        </div>

        // <div className="dropdown-menu">
        // {Object.keys(groupedOptions).map((group) => (
        //   <div key={group}>
        //     <div className="dropdown-group">{group}</div>
        //     <ul className="dropdown-list">
        //       {groupedOptions[group].map((option:any) => (
        //         <li
        //           key={option.label}
        //           onClick={() => handleOptionSelect(option)}
        //           className="dropdown-item"
        //         >
        //           {option.label}
        //         </li>
        //       ))}
        //     </ul>
        //   </div>
        // ))}
        // </div>
      )}
    </div>
  );
};
